import { Badge, IconButton, Stack } from "@mui/material";
import { useRouter } from "next/router";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import { useRecoilValue } from "recoil";
import { QuoteIfExistState } from "@/pages-components/quote-new/store/quote";
import { CartIcon } from "@/icons/cart-icon";
import { FrontDeskNav } from "@/widgets/frontdesk-nav/frontdesk-nav";
import { SystemNotifications } from "@/widgets/system-notifications-widget/system-notifications";
import {
  useTicketChatbot,
  useTicketNotifications,
} from "@/components/ticket-chatbot";
import { useTheme } from "@/contexts/theme";
import { SystemHelp } from "../system-help-and-support-widget/system-help-and-support-widget";
import { CustomTooltip } from "../custom-tooltip";
import { useStyle } from "../../style";
import { useHeader } from "../../use-header";


interface IHeaderActionsProps {
  /** Tightens icon sizing so the full action set fits a phone-width header. */
  compact?: boolean;
}

/**
 * The header action icons (FrontDesk, notifications, cart, support, help),
 * shared by the desktop header and the mobile header.
 */
const HeaderActions = ({ compact }: IHeaderActionsProps) => {
  const { primaryColor, secondColor } = useStyle();
  const { t, navigate } = useHeader();
  const router = useRouter();
  const QuoteIfExist = useRecoilValue<any>(QuoteIfExistState);
  const { openChatbot } = useTicketChatbot();
  const { unreadCount } = useTicketNotifications();
  const { currentTheme } = useTheme();
  const isHpClient = currentTheme === "hp-client";
  const iconButtonBackground = isHpClient ? "transparent" : "#f6f6f6";
  const iconSize = compact ? 18 : 22;
  const buttonStyle = {
    backgroundColor: iconButtonBackground,
    padding: compact ? "4px" : "8px",
  };
  // The mobile header sits on primaryColor(500); HP draws its icons on a
  // transparent chip, so their brand colors are unreadable there.
  const whiteIconsOnDarkHeader =
    compact && isHpClient
      ? {
          color: "#FFFFFF",
          "& [fill]:not([fill='none'])": { fill: "#FFFFFF" },
          "& [stroke]:not([stroke='none'])": { stroke: "#FFFFFF" },
        }
      : {};

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        gap: compact ? "2px" : "4px",
        ...(compact && {
          "& svg": {
            width: iconSize,
            height: iconSize,
            ...whiteIconsOnDarkHeader,
          },
        }),
      }}
    >
      <FrontDeskNav compact={compact} />
      <SystemNotifications compact={compact} />
      {QuoteIfExist == true && router.pathname !== "/quote" && (
        <IconButton
          className="gm-topbar-cart"
          style={buttonStyle}
          onClick={() => navigate("/quote")}
        >
          <CartIcon color={secondColor(500)} />
        </IconButton>
      )}
      {!isHpClient && (
        <CustomTooltip
          title={t("tickets.support") || "Support Desk"}
          arrow
          placement="bottom"
        >
          <IconButton data-gm-action="headset" style={buttonStyle} onClick={openChatbot}>
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  height: 18,
                  minWidth: 18,
                  right: 4,
                  top: 4,
                },
              }}
            >
              <HeadsetMicOutlinedIcon
                style={{
                  width: iconSize,
                  height: iconSize,
                  color: primaryColor(500),
                }}
              />
            </Badge>
          </IconButton>
        </CustomTooltip>
      )}
      <SystemHelp compact={compact} />
    </Stack>
  );
};

export { HeaderActions };
